/**
 * Konkord: an AudioContext that runs slower than real time sends less sound
 * than it receives, and the voice breaks up for everyone listening. Seen on
 * Hudson's PC: the microphone and the stream's program sound both fell to
 * 2/3 of real time at the same second (LiveKit's sender reports), with no
 * packet loss. Chromium renders a context on a timer instead of the sound
 * card in some cases (silent output, an output device that stops), and on
 * Windows that timer can tick at 15.6 ms instead of 10 ms: 64 %.
 *
 * Two defences: an inaudible signal on the context's output, so it never
 * counts as silent; and a watch on the context's clock, so the caller can
 * send the microphone untouched while the context lags.
 */

/** how often the context's clock is compared with the wall clock */
const CHECK_MS = 1000;
/** below this share of real time the context is lagging */
const SLOW = 0.9;
/** back to normal at this share */
const RECOVERED = 0.98;
/** checks in a row needed to switch (one hiccup is not a lag) */
const SLOW_CHECKS = 2;
const RECOVERED_CHECKS = 3;

/**
 * Konkord: far below one step of 24 bit output (6e-8 is 2^-24), never heard,
 * but never exactly zero either
 */
const KEEP_ALIVE_LEVEL = 1e-8;

/**
 * Konkord: the state machine of the watch, separate from the timer for tests
 * @param onChange Called when the context starts or stops lagging
 * @returns A function to feed with the wall clock (ms) and the context's
 * clock (s) at each check
 */
export function createClockWatch(onChange: (slow: boolean) => void) {
  let last: { wall: number; audio: number } | undefined;
  let slow = false;
  let bad = 0;
  let good = 0;

  return (wall: number, audio: number, running = true) => {
    const previous = last;
    last = { wall, audio };
    // a suspended context (no gesture yet, closing) is not lagging
    if (!running || !previous) {
      bad = good = 0;
      return;
    }
    const elapsed = (wall - previous.wall) / 1000;
    if (elapsed <= 0) return;
    const share = (audio - previous.audio) / elapsed;

    if (share < SLOW) {
      good = 0;
      if (++bad >= SLOW_CHECKS && !slow) {
        slow = true;
        onChange(true);
      }
    } else if (share >= RECOVERED) {
      bad = 0;
      if (slow && ++good >= RECOVERED_CHECKS) {
        slow = false;
        onChange(false);
      }
    }
  };
}

/**
 * Konkord: keep a context on the sound card and tell when its clock lags
 * @param context Context to watch
 * @param onChange Called when it starts or stops lagging
 * @returns Stop watching (the keep-alive signal goes with the context)
 */
export function watchAudioClock(
  context: AudioContext,
  onChange: (slow: boolean) => void,
) {
  const keepAlive = context.createConstantSource();
  keepAlive.offset.value = KEEP_ALIVE_LEVEL;
  keepAlive.connect(context.destination);
  keepAlive.start();

  const check = createClockWatch(onChange);
  const timer = setInterval(
    () =>
      check(
        performance.now(),
        context.currentTime,
        context.state === "running",
      ),
    CHECK_MS,
  );

  return () => {
    clearInterval(timer);
    try {
      keepAlive.stop();
      keepAlive.disconnect();
    } catch {
      // the context is already closed
    }
  };
}
